import { AttachInternals, Component, Element, Event, EventEmitter, h, Host, Prop, Watch, State, Listen } from '@stencil/core';
import { RegisterComponent } from '@/common/register-component';
import { LANGUAGE_CODE, TIMEZONE_MAP } from '@/common/constants';
import { Configuration, SpecificTimeAvailability, ThemeConfig } from '@nylas/core';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { debug, convertTo12HourFormat, convertTo24HourFormat, timeToMinutes } from '@/utils/utils';
import i18next from '@/utils/i18n';

@Component({
  tag: 'nylas-specific-time-availability-picker',
  styleUrl: 'nylas-specific-time-availability-picker.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasSpecificTimeAvailabilityPicker {
  /**
   * The element <nylas-specific-time-availability-picker> itself.
   */
  @Element() host!: HTMLNylasSpecificTimeAvailabilityPickerElement;

  /**
   * @standalone
   * The name of the specific date availability picker.
   */
  @Prop() name: string = 'specific-time-availability';

  /**
   * @standalone
   * The selected configuration.
   */
  @Prop() selectedConfiguration?: Configuration;

  /**
   * @standalone
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;

  /**
   * @standalone
   * The specific date open hours.
   */
  @Prop() specificTimesAvailability?: SpecificTimeAvailability[];

  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * @standalone
   * Hide the header of the specific time availability picker.
   */
  @Prop() hideHeader: boolean = false;

  /**
   * @standalone
   * The default timezone or preset timezone.
   */
  @Prop() defaultTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  /**
   * @standalone
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * The event is fired when the specific date open hours change.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * Internal state for managing specific date entries
   */
  @State() specificDateEntries: Array<{
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    isUnavailable: boolean;
  }> = [];

  /**
   * State for tracking validation errors
   */
  @State() validationErrors: { [key: string]: string } = {};

  /**
   * The selected timezone state.
   */
  @State() timezone: string = '';

  connectedCallback() {
    debug('nylas-specific-time-availability-picker', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-specific-time-availability-picker', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-specific-time-availability-picker', 'componentWillLoad');
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-specific-time-availability-picker', 'componentDidLoad');

    // Initialize timezone
    if (this.selectedConfiguration) {
      this.timezone = this.selectedConfiguration?.event_booking?.timezone || this.defaultTimezone;
      this.configChangedHandler(this.selectedConfiguration);
    } else {
      this.timezone = this.defaultTimezone;
      if (this.specificTimesAvailability && this.specificTimesAvailability.length > 0) {
        this.loadSpecificTimeAvailability();
      }
    }
  }

  get isInternalsAvailable() {
    return typeof this.internals.setFormValue === 'function';
  }

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-specific-time-availability-picker', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('defaultTimezone')
  defaultTimezoneChanged(newValue: string) {
    if (newValue && newValue !== this.timezone) {
      this.timezone = newValue;
      this.emitValueChanged();
    }
  }

  @Watch('selectedConfiguration')
  configChangedHandler(newConfig: Configuration) {
    debug('nylas-specific-time-availability-picker', 'configChangedHandler', newConfig);
    // Load specific date open hours from the configuration participants
    this.loadSpecificTimeAvailability();
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  loadSpecificTimeAvailability() {
    if (!this.specificTimesAvailability || this.specificTimesAvailability.length === 0) {
      return;
    }

    // If timezone is provided in the first entry, use it
    if (this.specificTimesAvailability[0].timezone) {
      this.timezone = this.specificTimesAvailability[0].timezone;
    }

    const entries = this.specificTimesAvailability.map((item, index) => {
      // Check if both start and end are "00:00" - this indicates an unavailable day
      const isUnavailable = item.start === '00:00' && item.end === '00:00';

      return {
        id: `specific-time-${index}`,
        date: item.date,
        startTime: isUnavailable ? '09:00am' : convertTo12HourFormat(item.start),
        endTime: isUnavailable ? '05:00pm' : convertTo12HourFormat(item.end),
        isUnavailable,
      };
    });

    this.specificDateEntries = entries;
    this.emitValueChanged();
  }

  addSpecificTimeAvailability() {
    // Initialize with incremental dates based on existing entries
    // First entry: today, second entry: tomorrow, third entry: day after, etc.
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + this.specificDateEntries.length);
    const formattedDate = targetDate.toISOString().split('T')[0];

    const newEntry = {
      id: `specific-time-${Date.now()}`,
      date: formattedDate,
      startTime: '09:00am',
      endTime: '05:00pm',
      isUnavailable: false,
      timezone: this.timezone,
    };

    this.specificDateEntries = [...this.specificDateEntries, newEntry];

    // Clear any global validation errors since we're adding a new valid entry
    if (this.isInternalsAvailable) {
      this.internals.setValidity({ customError: false });
    }

    this.emitValueChanged();
  }

  removeSpecificTimeAvailability(id: string) {
    this.specificDateEntries = this.specificDateEntries.filter(entry => entry.id !== id);

    // Clear validation error for the removed entry
    if (this.validationErrors[id]) {
      const newErrors = { ...this.validationErrors };
      delete newErrors[id];
      this.validationErrors = newErrors;
    }

    this.emitValueChanged();
  }

  toggleAvailability(id: string) {
    this.specificDateEntries = this.specificDateEntries.map(entry => {
      if (entry.id === id) {
        return { ...entry, isUnavailable: !entry.isUnavailable };
      }
      return entry;
    });

    // Clear any existing validation error for this entry since availability state changed
    if (this.validationErrors[id]) {
      const newErrors = { ...this.validationErrors };
      delete newErrors[id];
      this.validationErrors = newErrors;
    }

    this.emitValueChanged();
  }

  /**
   * Validates a single date entry
   */
  validateEntry(entry: (typeof this.specificDateEntries)[0]): string | null {
    // Check if date is empty or invalid
    if (!entry.date) {
      return i18next.t('nylasSpecificTimeAvailabilityPicker.errors.dateRequired');
    }

    const dateObj = new Date(entry.date);
    if (isNaN(dateObj.getTime())) {
      return i18next.t('nylasSpecificTimeAvailabilityPicker.errors.invalidDateFormat');
    }

    // If unavailable, no need to validate times
    if (entry.isUnavailable) {
      return null;
    }

    // Validate start and end times are present
    if (!entry.startTime || !entry.endTime) {
      return i18next.t('nylasSpecificTimeAvailabilityPicker.errors.timesRequired');
    }

    // Validate start time is before end time
    const startMinutes = timeToMinutes(entry.startTime);
    const endMinutes = timeToMinutes(entry.endTime);

    if (startMinutes >= endMinutes) {
      return i18next.t('nylasSpecificTimeAvailabilityPicker.errors.startTimeBeforeEndTime');
    }

    return null;
  }

  /**
   * Validates that timezone is set when there are entries
   */
  validateTimezone(): string | null {
    if (this.specificDateEntries.length > 0 && !this.timezone) {
      return i18next.t('nylasSpecificTimeAvailabilityPicker.errors.timezoneRequired');
    }
    return null;
  }

  /**
   * Validates all entries and updates validation state
   */
  validateAllEntries() {
    const errors: { [key: string]: string } = {};
    let hasErrors = false;

    // Validate timezone first
    const timezoneError = this.validateTimezone();
    if (timezoneError) {
      errors['timezone'] = timezoneError;
      hasErrors = true;
    }

    this.specificDateEntries.forEach(entry => {
      const error = this.validateEntry(entry);
      if (error) {
        errors[entry.id] = error;
        hasErrors = true;
      }
    });

    this.validationErrors = errors;

    // Update form validity
    if (hasErrors) {
      // Prioritize timezone error, then entry errors
      const firstErrorKey = errors['timezone'] ? 'timezone' : Object.keys(errors)[0];
      const firstErrorElement =
        firstErrorKey === 'timezone'
          ? (this.host.shadowRoot?.querySelector('.timezone-selector') as HTMLElement)
          : (this.host.shadowRoot?.querySelector(`[data-entry-id="${firstErrorKey}"]`) as HTMLElement);

      if (this.isInternalsAvailable && firstErrorElement) {
        this.internals.setValidity({ customError: true }, 'Please fix validation errors', firstErrorElement);
      }
    } else {
      if (this.isInternalsAvailable) {
        this.internals.setValidity({ customError: false });
      }
    }

    return !hasErrors;
  }

  @Listen('timeChange')
  handleTimeChange(event: CustomEvent<{ key: string; value: string }>) {
    const { key, value } = event.detail;
    const [entryId, timeType] = key.split('_');

    this.specificDateEntries = this.specificDateEntries.map(entry => {
      if (entry.id === entryId) {
        return {
          ...entry,
          [timeType === 'start' ? 'startTime' : 'endTime']: value,
        };
      }
      return entry;
    });

    // Clear any existing validation error for this entry
    if (this.validationErrors[entryId]) {
      const newErrors = { ...this.validationErrors };
      delete newErrors[entryId];
      this.validationErrors = newErrors;
    }

    // Clear form validity errors related to time
    if (this.isInternalsAvailable) {
      this.internals.setValidity({ customError: false });
    }

    this.emitValueChanged();
  }

  @Listen('timeWindowFormError')
  setFormError(event: CustomEvent<{ key: string; message: string }>) {
    const { key, message } = event.detail;
    const [entryId] = key.split('_');

    if (message) {
      // Set validation error for this entry
      this.validationErrors = {
        ...this.validationErrors,
        [entryId]: message,
      };

      const element = this.host.shadowRoot?.getElementById(key);
      if (element && this.isInternalsAvailable) {
        this.internals.setValidity({ customError: true }, message, element as HTMLInputElement);
      }
    } else {
      // Clear validation error for this entry
      const newErrors = { ...this.validationErrors };
      delete newErrors[entryId];
      this.validationErrors = newErrors;

      if (this.isInternalsAvailable) {
        this.internals.setValidity({ customError: false });
      }
    }
  }

  @Listen('nylasFormDropdownChanged')
  nylasFormDropdownChangedHandler(
    event: CustomEvent<{
      value: string;
      name: string;
    }>,
  ) {
    const { name, value } = event.detail;
    if (name === 'specific-time-timezone') {
      this.timezone = value;

      // Clear timezone validation error if it exists
      if (this.validationErrors['timezone']) {
        const newErrors = { ...this.validationErrors };
        delete newErrors['timezone'];
        this.validationErrors = newErrors;
      }

      this.emitValueChanged();
    }
  }

  @Listen('nylasFormInputChanged')
  handleFormInputChange(event: CustomEvent<{ value: string; name: string; label: string; type: string; error: string }>) {
    const { name, value } = event.detail;

    // Check if this is a date input for our component
    if (name.startsWith('date-') && name.includes('specific-time-')) {
      const entryId = name.replace('date-', '');

      this.specificDateEntries = this.specificDateEntries.map(entry => {
        if (entry.id === entryId) {
          return { ...entry, date: value };
        }
        return entry;
      });

      // Clear any existing validation error for this entry
      if (this.validationErrors[entryId]) {
        const newErrors = { ...this.validationErrors };
        delete newErrors[entryId];
        this.validationErrors = newErrors;
      }

      this.emitValueChanged();
    }
  }

  emitValueChanged() {
    // Validate all entries before emitting
    const isValid = this.validateAllEntries();

    const specificTimesAvailability = this.specificDateEntries
      .filter(entry => entry.date && (entry.isUnavailable || (entry.startTime && entry.endTime)))
      .map(entry => ({
        date: entry.date,
        start: entry.isUnavailable ? '00:00' : convertTo24HourFormat(entry.startTime),
        end: entry.isUnavailable ? '00:00' : convertTo24HourFormat(entry.endTime),
        timezone: this.timezone,
      }));

    const value = JSON.stringify(specificTimesAvailability);

    if (this.isInternalsAvailable && isValid) {
      this.internals.setFormValue(value, this.name);
    }

    this.valueChanged.emit({
      value,
      name: this.name,
    });
  }

  @RegisterComponent<NylasSpecificTimeAvailabilityPicker, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-specific-time-availability-picker',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    fireRegisterEvent: true,
  })
  render() {
    const timezoneOptions = Object.keys(TIMEZONE_MAP).map(key => ({
      label: TIMEZONE_MAP[key],
      value: key,
    }));
    const selectedTimezoneOption = timezoneOptions.find(i => i.value === this.timezone);

    return (
      <Host>
        <div class="nylas-specific-time-availability-picker" part="nstap">
          {!this.hideHeader && (
            <div class="header" part="nstap__header">
              <h3>{i18next.t('nylasSpecificTimeAvailabilityPicker.headerTitle')}</h3>
              <p>
                {i18next.t('nylasSpecificTimeAvailabilityPicker.headerDescription')}
                <tooltip-component>
                  <info-icon slot="tooltip-icon" />
                  <span slot="tooltip-content">{i18next.t('nylasSpecificTimeAvailabilityPicker.tooltip.desc')}</span>
                </tooltip-component>
              </p>
            </div>
          )}
          <div class="content" part="nstap__content">
            <section class="timezone-selector" part="nstap__timezone-selector" aria-labelledby="timezone-header">
              <h4 id="timezone-header" class="sub-header">
                {i18next.t('nylasSpecificTimeAvailabilityPicker.selectTimezone')}
              </h4>
              <p class="timezone-description">{i18next.t('nylasSpecificTimeAvailabilityPicker.timezoneDescription')}</p>
              {selectedTimezoneOption?.label && (
                <select-dropdown
                  name="specific-time-timezone"
                  searchPlaceholder={i18next.t('search')}
                  exportparts="sd_dropdown: nstap__timezone-container, sd_dropdown-button: nstap__timezone-button, sd_dropdown-content: nstap__timezone-dropdown-content"
                  options={timezoneOptions}
                  defaultSelectedOption={selectedTimezoneOption}
                  themeConfig={this.themeConfig}
                  aria-label={i18next.t('nylasSpecificTimeAvailabilityPicker.selectTimezone')}
                >
                  <span slot="select-icon" aria-hidden="true">
                    <globe-icon width="20" height="20" />
                  </span>
                </select-dropdown>
              )}
              {this.validationErrors['timezone'] && (
                <div class="error-message timezone-error" part="nstap__error-message" role="alert" aria-live="polite">
                  {this.validationErrors['timezone']}
                </div>
              )}
            </section>
            <section class="specific-times-section" part="nstap__specific-times-section" aria-labelledby="specific-times-header">
              <h4 id="specific-times-header" class="section-header">
                {i18next.t('nylasSpecificTimeAvailabilityPicker.specificTimesHeader')}
              </h4>
              <p class="section-description">{i18next.t('nylasSpecificTimeAvailabilityPicker.specificTimesDescription')}</p>
              <div class="specific-times" part="nstap__specific-times" role="list">
                {this.specificDateEntries.length === 0 ? (
                  <div class="empty-state" part="nstap__empty-state">
                    <div class="empty-icon" part="nstap__empty-icon">
                      <calendar-icon width="32" height="32" />
                    </div>
                    <h3 class="empty-title" part="nstap__empty-title">
                      {i18next.t('nylasSpecificTimeAvailabilityPicker.emptyState.title')}
                    </h3>
                    <p class="empty-subtitle" part="nstap__empty-subtitle">
                      {i18next.t('nylasSpecificTimeAvailabilityPicker.emptyState.subtitle')}
                    </p>
                  </div>
                ) : (
                  this.specificDateEntries.map((entry, index) => (
                    <article
                      class="specific-time-entry"
                      part="nstap__specific-time-entry"
                      key={entry.id}
                      data-entry-id={entry.id}
                      role="listitem"
                      aria-label={`Specific time availability entry ${index + 1}`}
                    >
                      <div class="entry-content" part="nstap__entry-content">
                        <div class="date-input-container" part="nstap__date-input-container">
                          <nylas-date-component
                            name={`date-${entry.id}`}
                            defaultValue={entry.date}
                            placeholder="yyyy-mm-dd"
                            part="nstap__date-input"
                            class="date-input"
                            themeConfig={this.themeConfig}
                            exportparts="ic__date: nstap__date-field, ic__input_wrapper: nstap__date-wrapper, ic__label: nstap__date-label"
                            aria-label={`Date for entry ${index + 1}`}
                          />
                        </div>
                        {entry.isUnavailable ? (
                          <div class="unavailable-text" part="nstap__unavailable-text" role="status">
                            <span class="unavailable-label">{i18next.t('nylasSpecificTimeAvailabilityPicker.entireDayUnavailable')}</span>
                          </div>
                        ) : (
                          <div class="time-pickers" part="nstap__time-pickers" role="group" aria-label="Time range">
                            <nylas-time-window-picker
                              id={`${entry.id}_start`}
                              time={entry.startTime}
                              name={`${entry.id}_start`}
                              key={`${entry.id}_start`}
                              language={this.selectedLanguage}
                              hasError={!!this.validationErrors[entry.id]}
                              themeConfig={this.themeConfig}
                              exportparts="time-picker: nstap__time-picker-container, time-input: nstap__time-picker-input, times: nstap__time-picker-times"
                              aria-label={`Start time for entry ${index + 1}`}
                            />
                            <span class="time-separator" aria-hidden="true">
                              -
                            </span>
                            <nylas-time-window-picker
                              id={`${entry.id}_end`}
                              time={entry.endTime}
                              name={`${entry.id}_end`}
                              key={`${entry.id}_end`}
                              minimumStartTime={entry.startTime}
                              language={this.selectedLanguage}
                              hasError={!!this.validationErrors[entry.id]}
                              themeConfig={this.themeConfig}
                              exportparts="time-picker: nstap__time-picker-container, time-input: nstap__time-picker-input, times: nstap__time-picker-times"
                              aria-label={`End time for entry ${index + 1}`}
                            />
                          </div>
                        )}
                        <div class="action-buttons" part="nstap__action-buttons">
                          <button
                            type="button"
                            class={{ 'toggle-button': true, 'unavailable': entry.isUnavailable }}
                            part="nstap__toggle-button"
                            onClick={() => this.toggleAvailability(entry.id)}
                            aria-label={
                              entry.isUnavailable
                                ? i18next.t('nylasSpecificTimeAvailabilityPicker.markAsAvailable')
                                : i18next.t('nylasSpecificTimeAvailabilityPicker.markAsUnavailable')
                            }
                            title={
                              entry.isUnavailable
                                ? i18next.t('nylasSpecificTimeAvailabilityPicker.markAsAvailable')
                                : i18next.t('nylasSpecificTimeAvailabilityPicker.markAsUnavailable')
                            }
                          >
                            <calendar-icon width="20" height="20" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            class="remove-button"
                            part="nstap__remove-button"
                            onClick={() => this.removeSpecificTimeAvailability(entry.id)}
                            aria-label={`Remove specific time entry ${index + 1}`}
                            title="Remove"
                          >
                            <close-icon width="16" height="16" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      {this.validationErrors[entry.id] && (
                        <div class="error-message" part="nstap__error-message" role="alert" aria-live="polite">
                          {this.validationErrors[entry.id]}
                        </div>
                      )}
                    </article>
                  ))
                )}
              </div>
              <div class="add-button-container" part="nstap__add-button-container">
                <button-component
                  id="add-specific-time-open-hour"
                  onClick={() => this.addSpecificTimeAvailability()}
                  part="nstap__add-button"
                  variant="basic"
                  aria-label={i18next.t('nylasSpecificTimeAvailabilityPicker.addSpecificTimeAvailability')}
                >
                  <add-circle-icon width="24" height="24" aria-hidden="true" />
                  {i18next.t('nylasSpecificTimeAvailabilityPicker.addSpecificTimeAvailability')}
                </button-component>
              </div>
            </section>
          </div>
        </div>
      </Host>
    );
  }
}
